import { checkoutPayFlowController } from '@cart-services/core/checkoutPayFlowController'
import { type CreditCardDialogContext } from '@cart-services/hooks/credit-card-dialog-types'
import { type AlipayDialogContext } from '@cart-services/hooks/use-alipay-dialog-flow-actions'
import { type ApplePayDialogContext } from '@cart-services/hooks/useApplePayDialogActions'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { BaseCheckoutPayFlowModule } from '@payments/lib/checkout-pay/BaseCheckoutPayFlowModule'
import { CheckoutPayAlipayHkModule } from '@payments/lib/checkout-pay/CheckoutPayAlipayHkModule'
import { CheckoutPayApplePayModule } from '@payments/lib/checkout-pay/CheckoutPayApplePayModule'
import { useEffect, useState } from 'react'
import { type AppliedPaymentModule } from '../utils/utility-process-pay-module'

export interface UseFpB2bCkoFlowPaymentParams {
  paymentModule: AppliedPaymentModule | undefined
}

export interface CkoFlowExecuteArgs {
  paymentTransactionId: string
  paymentOptionId: number
  paymentSuccessUrl: string
  paymentFallbackUrl: string
  email?: string
}

export interface OpenCreditCardDialogArgs {
  paymentTransactionId: string
  orderHash?: string | null
  paymentOptionId: number
  paymentSuccessUrl: string
  paymentFallbackUrl: string
  email?: string
  rememberPaymentCardToken?: boolean
}

/**
 * B2B CKO Flow 付款彈窗匯流 hook。只負責開對應付款彈窗 context（Apple Pay / AliPayHK / 信用卡）;
 * SDK 掛載 / submit / execute 由各 dialog flow hook 在彈窗內處理。token-based 路線由 caller 處理。
 */
export const useFpB2bCkoFlowPayment = ({ paymentModule }: UseFpB2bCkoFlowPaymentParams) => {
  const [applePayDialogContext, setApplePayDialogContext] = useState<ApplePayDialogContext | null>(null)
  const [alipayDialogContext, setAlipayDialogContext] = useState<AlipayDialogContext | null>(null)
  const [creditCardDialogContext, setCreditCardDialogContext] = useState<CreditCardDialogContext | null>(null)

  // 沒 bind 的話 SDK submit 成功也不會 resolve deferred → submitAndAwaitPaymentId hang → execution 不會被呼叫
  useEffect(function bindCheckoutPayFlowController() {
    if (!(paymentModule instanceof BaseCheckoutPayFlowModule)) return
    const unbind = checkoutPayFlowController.bind(paymentModule)
    return unbind
  }, [paymentModule])

  const openApplePayDialog = useCallbackEvent((context: ApplePayDialogContext) => {
    setApplePayDialogContext(context)
  })

  const openAlipayDialog = useCallbackEvent((context: AlipayDialogContext) => {
    setAlipayDialogContext(context)
  })

  const openCreditCardDialogForTransaction = useCallbackEvent((args: OpenCreditCardDialogArgs) => {
    setCreditCardDialogContext({
      paymentTransactionId: args.paymentTransactionId,
      orderHash: args.orderHash ?? null,
      paymentSuccessUrl: args.paymentSuccessUrl,
      paymentFallbackUrl: args.paymentFallbackUrl,
      paymentOptionId: args.paymentOptionId,
      email: args.email,
      rememberPaymentCardToken: args.rememberPaymentCardToken,
    })
  })

  const execute = useCallbackEvent(async (args: CkoFlowExecuteArgs): Promise<void> => {
    if (paymentModule instanceof CheckoutPayApplePayModule) {
      openApplePayDialog({
        paymentTransactionId: args.paymentTransactionId,
        orderHash: null,
        paymentSuccessUrl: args.paymentSuccessUrl,
        paymentFallbackUrl: args.paymentFallbackUrl,
        paymentOptionId: args.paymentOptionId,
      })
      return
    }
    if (paymentModule instanceof CheckoutPayAlipayHkModule) {
      openAlipayDialog({
        paymentTransactionId: args.paymentTransactionId,
        orderHash: null,
        paymentSuccessUrl: args.paymentSuccessUrl,
        paymentFallbackUrl: args.paymentFallbackUrl,
        paymentOptionId: args.paymentOptionId,
        email: args.email,
      })
    }
  })

  const closeApplePayDialog = useCallbackEvent(() => {
    setApplePayDialogContext(null)
  })

  const closeAlipayDialog = useCallbackEvent(() => {
    setAlipayDialogContext(null)
  })

  const closeCreditCardDialog = useCallbackEvent(() => {
    setCreditCardDialogContext(null)
  })

  return {
    execute,
    openApplePayDialog,
    openAlipayDialog,
    openCreditCardDialogForTransaction,
    applePayDialogContext,
    closeApplePayDialog,
    alipayDialogContext,
    closeAlipayDialog,
    creditCardDialogContext,
    closeCreditCardDialog,
  }
}
